import React, { type FC, useEffect, useRef } from 'react';
import { geoDistance, geoGraticule10, geoOrthographic, geoPath } from 'd3-geo';
import { feature } from 'topojson-client';
import type { Topology, GeometryCollection } from 'topojson-specification';
import landTopo from 'world-atlas/land-110m.json';
import type { GrowthSignup } from './liveSignups';

const land = feature(
  landTopo as unknown as Topology<{ land: GeometryCollection }>,
  (landTopo as unknown as Topology<{ land: GeometryCollection }>).objects.land,
);

const PIN_LIFE_MS = 10 * 60 * 1000;
const RIPPLE_MS = 3000;

interface Pin {
  lng: number;
  lat: number;
  at: number;
}

/**
 * Slowly spinning globe. Each new sign-up with coordinates drops a pin and
 * swings the globe to it; pins fade after ten minutes.
 */
const SignupGlobe: FC<{ signups: GrowthSignup[] }> = ({ signups }) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const pinsRef = useRef<Pin[]>([]);
  const rotRef = useRef<[number, number]>([0, -25]);
  const targetRef = useRef<[number, number] | null>(null);
  const seenRef = useRef<Set<string>>(new Set());

  useEffect(() => {
    const now = Date.now();
    for (const s of [...signups].reverse()) {
      if (seenRef.current.has(s.sessionId) || s.lat == null || s.lng == null)
        continue;
      seenRef.current.add(s.sessionId);
      const at = Math.min(Date.parse(s.completedAt) || now, now);
      pinsRef.current.push({ lng: s.lng, lat: s.lat, at });
      if (now - at < RIPPLE_MS * 2) targetRef.current = [-s.lng, -s.lat];
    }
  }, [signups]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    let raf = 0;

    const draw = () => {
      const parent = canvas.parentElement;
      const size = Math.max(
        120,
        Math.min(parent?.clientWidth ?? 300, parent?.clientHeight ?? 300),
      );
      const dpr = window.devicePixelRatio || 1;
      if (canvas.width !== size * dpr) {
        canvas.width = size * dpr;
        canvas.height = size * dpr;
        canvas.style.width = `${size}px`;
        canvas.style.height = `${size}px`;
      }
      const s = canvas.width;
      const r = s / 2 - 6 * dpr;
      const rot = rotRef.current;
      const target = targetRef.current;
      if (target) {
        rot[0] += (target[0] - rot[0]) * 0.06;
        rot[1] += (target[1] - rot[1]) * 0.06;
        if (Math.abs(target[0] - rot[0]) < 0.3) targetRef.current = null;
      } else {
        rot[0] += 0.08;
      }
      const proj = geoOrthographic()
        .translate([s / 2, s / 2])
        .scale(r)
        .rotate(rot)
        .clipAngle(90);
      const path = geoPath(proj, ctx);

      ctx.clearRect(0, 0, s, s);
      ctx.beginPath();
      ctx.arc(s / 2, s / 2, r, 0, Math.PI * 2);
      ctx.fillStyle = '#eef2ff';
      ctx.fill();
      ctx.strokeStyle = '#c7d2fe';
      ctx.lineWidth = 1.5 * dpr;
      ctx.stroke();
      ctx.beginPath();
      path(geoGraticule10());
      ctx.strokeStyle = 'rgba(79, 70, 229, 0.12)';
      ctx.lineWidth = dpr;
      ctx.stroke();
      ctx.beginPath();
      path(land);
      ctx.fillStyle = '#c7d2fe';
      ctx.fill();
      ctx.strokeStyle = '#818cf8';
      ctx.lineWidth = dpr;
      ctx.stroke();

      const now = Date.now();
      pinsRef.current = pinsRef.current.filter((p) => now - p.at < PIN_LIFE_MS);
      for (const p of pinsRef.current) {
        if (geoDistance([p.lng, p.lat], [-rot[0], -rot[1]]) > Math.PI / 2)
          continue;
        const xy = proj([p.lng, p.lat]);
        if (!xy) continue;
        const age = now - p.at;
        if (age < RIPPLE_MS) {
          const t = age / RIPPLE_MS;
          ctx.beginPath();
          ctx.arc(xy[0], xy[1], (8 + t * 40) * dpr, 0, Math.PI * 2);
          ctx.strokeStyle = `rgba(22, 163, 74, ${1 - t})`;
          ctx.lineWidth = 3 * dpr;
          ctx.stroke();
        }
        ctx.beginPath();
        ctx.arc(xy[0], xy[1], (age < RIPPLE_MS ? 9 : 5) * dpr, 0, Math.PI * 2);
        ctx.fillStyle = age < RIPPLE_MS ? '#16a34a' : '#4f46e5';
        ctx.fill();
      }
      raf = requestAnimationFrame(draw);
    };
    raf = requestAnimationFrame(draw);
    return () => cancelAnimationFrame(raf);
  }, []);

  return <canvas ref={canvasRef} className='growth-globe-canvas' />;
};

export default SignupGlobe;
